import { useState, useEffect, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faSearch } from '@fortawesome/free-solid-svg-icons';
import s from '../../../pages/CaseFilePage/CaseFilePage.module.css';

interface SearchableSelectProps {
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export default function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  disabled = false,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: globalThis.MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [search, options]);

  const selectedLabel = options.find((o) => o.value === value)?.label || '';

  return (
    <div ref={wrapRef} className={s.searchableSelect}>
      <div
        className={`${s.searchableSelectTrigger} ${disabled ? s.searchableSelectDisabled : ''}`}
        onClick={() => {
          if (disabled) return;
          setOpen((p) => !p);
          setSearch('');
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
      >
        <span className={selectedLabel ? '' : s.searchableSelectPlaceholder}>
          {selectedLabel || placeholder}
        </span>
        <FontAwesomeIcon icon={faChevronDown} className={s.searchableSelectChevron} />
      </div>
      {open && (
        <div className={s.searchableSelectDropdown}>
          <div className={s.searchableSelectSearchWrap}>
            <FontAwesomeIcon icon={faSearch} className={s.searchableSelectSearchIcon} />
            <input
              ref={inputRef}
              className={s.searchableSelectSearchInput}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              autoFocus
            />
          </div>
          <div className={s.searchableSelectOptions}>
            <div
              className={`${s.searchableSelectOption} ${!value ? s.searchableSelectOptionActive : ''}`}
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
            >
              {placeholder}
            </div>
            {filtered.map((opt) => (
              <div
                key={opt.value}
                className={`${s.searchableSelectOption} ${opt.value === value ? s.searchableSelectOptionActive : ''}`}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
              >
                {opt.label}
              </div>
            ))}
            {filtered.length === 0 && <div className={s.searchableSelectNoResults}>No matches</div>}
          </div>
        </div>
      )}
    </div>
  );
}
